import React, { useCallback, useMemo, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { Invoice, UserRole } from '@/types'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { useFacturationStore } from '@/stores/facturation.store'
import { useAuth } from '@/hooks/useAuth'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { getFacturationColumnDefs } from '@/column-defs/facturation.column-defs'
import { InvoiceDetailModal } from './InvoiceDetailModal'
import { toastError } from '@/utils/toast'
import { hasAnyRole, NEO_PLATFORM_ROLES } from '@/utils/role-access'

export const FacturationTab: React.FC = () => {
  const invoices = useFacturationStore(state => state.invoices)
  const selectedInvoice = useFacturationStore(state => state.selectedInvoice)
  const isDetailLoading = useFacturationStore(state => state.isDetailLoading)
  const fetchInvoiceById = useFacturationStore(state => state.fetchInvoiceById)
  const clearSelectedInvoice = useFacturationStore(state => state.clearSelectedInvoice)
  const { user } = useAuth()

  const showEntityColumns = hasAnyRole(user, NEO_PLATFORM_ROLES)
  const showResellerGroups = user?.role === UserRole.WHOLESALER
  const showEntityInfo = showEntityColumns

  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const handleViewDetail = useCallback(
    async (invoice: Invoice) => {
      setIsDetailOpen(true)
      try {
        await fetchInvoiceById(invoice._id)
      } catch (error) {
        setIsDetailOpen(false)
        toastError(
          error instanceof Error
            ? error.message
            : 'Erreur lors du chargement du détail de la facture'
        )
      }
    },
    [fetchInvoiceById]
  )

  const handleCloseDetail = useCallback(() => {
    setIsDetailOpen(false)
    clearSelectedInvoice()
  }, [clearSelectedInvoice])

  const columnDefs: ColDef[] = useMemo(
    () =>
      getFacturationColumnDefs({
        handleViewDetail,
        showEntityColumns,
      }),
    [handleViewDetail, showEntityColumns]
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  const rowData = invoices ?? []

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Factures mensuelles
          </h2>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            Historique des factures émises par NEO Systems, avec détail par licence et lien vers le
            PDF Flux.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div
          className="ag-theme-alpine"
          style={{ height: '600px', width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <AGTableWrapper collection="facturation">
            <AgGridReact
              theme="legacy"
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressCellFocus={false}
              headerHeight={48}
              rowHeight={48}
              localeText={agGridLocaleFr}
            />
          </AGTableWrapper>
        </div>
      </div>

      {isDetailOpen && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          isLoading={isDetailLoading}
          showEntityInfo={showEntityInfo}
          showResellerGroups={showResellerGroups}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  )
}
